// The payment of each printed copy. The kiosk tells the backend which payment
// paid for a copy and what the copy cost. With a capture after the print, the
// card reader holds the amount and the backend captures only the printed
// copies: it does not take the amount of a failed copy. With a capture at the
// payment, the backend refunds the amount of a failed copy. These helpers are
// pure, thus the print and payment services and the screens share one rule.

export const REFUND_SUCCEEDED = 'succeeded';
// The copy state of the print poll for a copy whose refund Stripe took. Stripe
// can report a new refund as pending before it succeeds; the money comes back.
export const COPY_REFUNDED = 'refunded';
// The copy state and the refund status of a paid copy that did not print and
// whose payment is a hold: the capture does not take its amount.
export const COPY_NOT_CHARGED = 'not_charged';
// The capture mode of a payment intent of a declared order. An older backend
// does not send the field: then the kiosk captures at the payment.
export const CAPTURE_AFTER_PRINT = 'after_print';
export const COPY_PRINTED = 'printed';
// The copy state of a copy without a price that did not print.
export const COPY_FAILED = 'failed';

export interface PaymentAcceptance {
  status?: string;
  capture?: string;
}

// The result of the last send of one copy, by its idempotency key.
export interface CopyRefundRecord {
  // What the copy cost on its payment. Null: the copy went without a binding.
  amountCents: number | null;
  copyState?: string | null;
  refundStatus: string | null;
  refundAmountCents: number | null;
}

/**
 * What the next batch does with one copy, from the record of its key:
 * - new: no send under its key, thus the copy prints
 * - printed: the copy never prints again
 * - reprint: final without a print (not charged, refunded, or failed without
 *   a price). "Print again" prints it only under a new key.
 * - kept: a refund that failed (staff must help), or no final answer (the copy
 *   can still print under its key). The copy does not print again, and it
 *   keeps the staff text.
 */
export type CopySlot = 'new' | 'printed' | 'reprint' | 'kept';

export interface CopyPaymentPortrait {
  count: number;
  printedCopies?: number;
  status?: string;
  paymentIntentId?: string | null;
  copyRefunds?: Record<string, CopyRefundRecord> | null;
}

export interface CopyRefundSummary {
  refundedCents: number;
  notChargedCents: number;
  needsStaff: boolean;
}

export interface PrintedCopyCount {
  printed: number;
  total: number;
}

/**
 * True when the kiosk can print for a payment: the backend captured it, or the
 * reader holds the amount of a declared order until the copies print.
 */
export function isPaymentAccepted(
  paymentIntent: PaymentAcceptance | null | undefined
): boolean {
  if (paymentIntent?.status === 'succeeded') {
    return true;
  }
  return (
    paymentIntent?.status === 'requires_capture' &&
    paymentIntent.capture === CAPTURE_AFTER_PRINT
  );
}

/**
 * The amount of each copy of one payment, in minor units, in print order.
 *
 * The cart charges one copy at the first-copy price (the group price when the
 * queue has a group portrait) and each further copy at the copy price, then
 * rounds the total. The first copy of the first group portrait takes the
 * remainder, else the first copy of the queue. Thus the copies always add up
 * to the amount that was charged. When the copy price does not explain the
 * amount, the copies share it equally and the first copies take the
 * remainder.
 */
export function allocateCopyAmounts(
  portraits: readonly { count: number; isGroupPortrait?: boolean }[],
  totalCents: number,
  copyCents: number
): number[][] {
  const counts = portraits.map((portrait) =>
    Math.max(0, Math.floor(portrait.count || 0))
  );
  const copies = counts.reduce((sum, count) => sum + count, 0);
  if (copies === 0 || !Number.isInteger(totalCents) || totalCents < 0) {
    return counts.map((count) => new Array<number>(count).fill(0));
  }

  const firstCopyIndex = firstCopyPriceIndex(portraits, counts);
  const firstCopyCents = totalCents - (copies - 1) * copyCents;
  let flat = new Array<number>(copies).fill(copyCents);
  flat[firstCopyIndex] = firstCopyCents;
  if (!Number.isInteger(copyCents) || copyCents < 0 || firstCopyCents < 0) {
    const share = Math.floor(totalCents / copies);
    const remainder = totalCents - share * copies;
    flat = flat.map((_, index) => share + (index < remainder ? 1 : 0));
  }

  const amounts: number[][] = [];
  let offset = 0;
  for (const count of counts) {
    amounts.push(flat.slice(offset, offset + count));
    offset += count;
  }
  return amounts;
}

// The flat index of the copy that the cart charges at the first-copy price.
function firstCopyPriceIndex(
  portraits: readonly { isGroupPortrait?: boolean }[],
  counts: readonly number[]
): number {
  let offset = 0;
  for (const [index, count] of counts.entries()) {
    if (count > 0 && portraits[index]?.isGroupPortrait) {
      return offset;
    }
    offset += count;
  }
  return 0;
}

function printedCopies(portrait: CopyPaymentPortrait): number {
  if (portrait.status === 'PRINTED') {
    return portrait.count;
  }
  return portrait.status === 'PARTIAL' ? portrait.printedCopies || 0 : 0;
}

function unprintedCopies(portrait: CopyPaymentPortrait): number {
  return Math.max(0, portrait.count - printedCopies(portrait));
}

function isPrinted(record: CopyRefundRecord): boolean {
  return record.copyState === COPY_PRINTED;
}

function isNotCharged(record: CopyRefundRecord): boolean {
  return (
    record.copyState === COPY_NOT_CHARGED ||
    record.refundStatus === COPY_NOT_CHARGED
  );
}

function isRefunded(record: CopyRefundRecord): boolean {
  return (
    !isNotCharged(record) &&
    (record.copyState === COPY_REFUNDED ||
      record.refundStatus === REFUND_SUCCEEDED)
  );
}

/** What the next batch does with the copy whose key has this record. */
export function copySlot(record: CopyRefundRecord | undefined): CopySlot {
  if (!record) {
    return 'new';
  }
  if (record.copyState === COPY_PRINTED) {
    return 'printed';
  }
  if (
    isNotCharged(record) ||
    isRefunded(record) ||
    record.copyState === COPY_FAILED
  ) {
    return 'reprint';
  }
  return 'kept';
}

/**
 * The amount that "Print again" takes for a copy whose slot is reprint: the
 * money that came back or that the capture did not take. A copy without a
 * price prints again without a payment (0).
 */
export function reprintAmount(record: CopyRefundRecord): number {
  if (isNotCharged(record)) {
    return record.amountCents ?? 0;
  }
  if (isRefunded(record)) {
    return record.refundAmountCents ?? record.amountCents ?? 0;
  }
  return 0;
}

/**
 * What the print-failed screen can tell the visitor. A refund counts when the
 * print poll ended the copy as refunded (Stripe took the refund). A copy that
 * the poll ended as not charged counts with the amount that the kiosk bound to
 * it, because the backend gives no refund amount for it. Each paid copy that
 * did not print and has neither result needs a staff member: its money did
 * not come back by itself. A copy that the kiosk sent without a price has no
 * money. O(n) in the copies of the queue.
 */
export function summarizeCopyRefunds(
  portraits: readonly CopyPaymentPortrait[]
): CopyRefundSummary {
  let refundedCents = 0;
  let notChargedCents = 0;
  let needsStaff = false;
  for (const portrait of portraits) {
    const records = Object.values(portrait.copyRefunds || {});
    let accountedFor = 0;
    for (const record of records) {
      if (isNotCharged(record)) {
        notChargedCents += record.amountCents ?? 0;
        accountedFor += 1;
      } else if (isRefunded(record)) {
        refundedCents += record.refundAmountCents ?? 0;
        accountedFor += 1;
      } else if (record.amountCents === 0 && !isPrinted(record)) {
        accountedFor += 1;
      }
    }
    if (portrait.paymentIntentId && accountedFor < unprintedCopies(portrait)) {
      needsStaff = true;
    }
  }
  return { refundedCents, notChargedCents, needsStaff };
}

/** The printed copies of the queue and all its copies. O(n) in the portraits. */
export function countPrintedCopies(
  portraits: readonly CopyPaymentPortrait[]
): PrintedCopyCount {
  let printed = 0;
  let total = 0;
  for (const portrait of portraits) {
    printed += printedCopies(portrait);
    total += portrait.count;
  }
  return { printed, total };
}
